/**
 * World generation core (the 1.18 "Caves & Cliffs" model).
 *
 * Terrain is a 3-D density field, not a heightmap: five climate noises
 * (temperature, humidity, continentalness, erosion, weirdness) are sampled
 * per column; continentalness / erosion / peaks-and-valleys go through
 * splines that give a surface offset and a 3-D noise amplitude, and a block
 * is solid where `offset - y + amplitude * noise3 > 0`. The same climate
 * point picks the biome (biomes.ts), so terrain and biome agree by
 * construction. Rivers are the weirdness valley band carved to sea level.
 *
 * 3-D noises are sampled on a 4-block lattice and interpolated, like vanilla.
 * Everything is seeded and pure — the same (seed, x, y, z) always produces
 * the same block on any thread — so features can cross chunk borders.
 */

import { CHUNK_SIZE, SEA_LEVEL, WORLD_HEIGHT } from '../constants';
import {
  AIR, BEDROCK_ID, CALCITE_ID, CLAY_ID, COARSE_DIRT_ID, DEEPSLATE_ID, DIRT_ID, GRASS_ID, GRAVEL_ID,
  ICE_ID, LAVA_ID, MUD_ID, MYCELIUM_ID, PACKED_ICE_ID, PODZOL_ID, POWDER_SNOW_ID, RED_SAND_ID,
  RED_SANDSTONE_ID, SAND_ID, SANDSTONE_ID, SNOW_BLOCK_ID, SNOW_ID, STONE_ID, TERRACOTTA_ID,
  TERRACOTTA_IDS, WATER_ID,
} from '../blocks';
import { fbm2, hash2, ridged2, Simplex } from '../noise';
import { Bed, Biome, BIOME_LIST, pickBiome, Surf, temperatureIndex } from './biomes';
import { isColdAt } from './biomeClimate';
import { type GenCtx, MARGIN, PSIZE, vy } from './ctx';
import { placeOres } from './ores';
import { decorate } from './features';
import { StructureGenerator, type StructureHit } from './structures/index';

export { Biome, BIOME_NAMES } from './biomes';

/** Bumped whenever generation output changes (invalidates saved edits). 7: overworld relief retuned to vanilla proportions (flat lowlands, shelved mountains, hill layer, fixed-width rivers, deeper oceans, islands, snow line by biome). 6: bigger caverns, flooded caves, ravines, lusher lush caves. 5: smoother End island. 4: trial chambers, stronghold silverfish spawner, nether bastions / ruined portals / debris curve, End cages, chorus, cities. */
export const WORLDGEN_VERSION = 7;

export interface ColumnInfo {
  /** Approximate surface y (the spline offset; 3-D noise moves the real one). */
  height: number;
  biome: Biome;
}

/** Lattice spacing of the interpolated 3-D noises. */
const CELL = 4;
const LXZ = PSIZE / CELL + 1;          // terrain lattice, padded area
const CXZ = CHUNK_SIZE / CELL + 1;     // cave lattice, chunk only
const LY = WORLD_HEIGHT / CELL + 1;

/** Stone turns to deepslate below here (vanilla Y 0..8), lava fills caves below LAVA_Y. */
const DEEPSLATE_Y = vy(0);
const LAVA_Y = vy(-54);
const MAX_SURFACE = WORLD_HEIGHT - 14;
const RIVER_BED = -3;
/** Values per cave lattice point: spaghetti a/b, noodle a/b, cheese, tunnel thickness. */
const CF = 6;

const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
function smooth(a: number, b: number, v: number): number {
  const t = clamp01((v - a) / (b - a));
  return t * t * (3 - 2 * t);
}
function table(x: number, xs: readonly number[], ys: readonly number[]): number {
  if (x <= xs[0]) return ys[0];
  for (let i = 1; i < xs.length; i++) {
    if (x < xs[i]) return ys[i - 1] + ((ys[i] - ys[i - 1]) * (x - xs[i - 1])) / (xs[i] - xs[i - 1]);
  }
  return ys[ys.length - 1];
}

/** RMS of Simplex.noise2, used to normalise the climate noises. */
const SIMPLEX_RMS = 0.444;

/**
 * A vanilla-style octave noise: `amps[i]` weights the octave whose period is
 * `period / 2^i` blocks. Output is scaled to the standard deviation the
 * biome interval tables expect.
 */
class Climate {
  private readonly noise: Simplex;
  private readonly freq: number[] = [];
  private readonly weight: number[] = [];

  constructor(seed: number, period: number, amps: readonly number[], std: number) {
    this.noise = new Simplex(seed);
    let power = 0;
    for (let i = 0; i < amps.length; i++) {
      if (amps[i] === 0) continue;
      const w = amps[i] / (1 << i);
      this.freq.push((1 << i) / period);
      this.weight.push(w);
      power += w * w;
    }
    const gain = std / (SIMPLEX_RMS * Math.sqrt(power));
    for (let i = 0; i < this.weight.length; i++) this.weight[i] *= gain;
  }

  get(x: number, z: number): number {
    let sum = 0;
    for (let i = 0; i < this.freq.length; i++) {
      const f = this.freq[i];
      sum += this.weight[i] * this.noise.noise2(x * f + i * 37.17, z * f - i * 53.71);
    }
    return sum;
  }
}

// Surface level over erosion (blocks above sea level). Low erosion is
// mountain country, high erosion is flat. The knots sit so each erosion band
// of the biome table is a shelf with a short ramp between shelves: lowlands
// stay low and the ground steps up to plateaus and mountains rather than
// swelling everywhere. level = L_COAST + L_INLAND * inland + L_FAR * far.
const E_X = [-1, -0.78, -0.62, -0.5, -0.375, -0.3, -0.2225, -0.16, 0.05, 0.12, 0.45, 0.55, 0.62, 1];
const L_COAST = [11, 10, 8.5, 6.5, 5.5, 5, 4.5, 4.2, 4, 3, 3, 3, 2.4, 2.2];
const L_INLAND = [28, 26, 22, 11, 7, 5.5, 5, 4.3, 4, 2.5, 2, 2, 0.3, 0.2];
const L_FAR = [10, 10, 10, 10, 10, 10, 9, 1, 0, 0, 0, 0, 0, 0];
// Extra height on weirdness ridges: P_COAST + P_INLAND * inland, times ridge^1.5.
const P_COAST = [14, 12, 9, 4, 3, 2.5, 2.2, 2, 1.8, 1, 1, 1, 0, 0];
const P_INLAND = [42, 40, 32, 10, 5, 4, 3, 2.5, 2, 1, 0.8, 0.8, 0, 0];
// Amplitude of the hill layer (knolls and dips tens of blocks wide).
const HILLS = [6, 6, 6, 6, 5, 4.5, 4, 3.5, 3, 1.6, 1.4, 2, 0.9, 0.8];
// Sea floor over continentalness: mushroom islands, deep ocean, ocean, shelf, shore.
const OCEAN_X = [-1.2, -1.05, -1.0, -0.6, -0.455, -0.4, -0.25, -0.19, -0.15, -0.11];
const OCEAN_Y = [3, 3, -30, -30, -24, -14, -11, -5, -1.5, 1];
/** Half width (blocks) of a river's biome strip, and of the carved channel's ramp. */
const RIVER_HALF = 5.5;
const CHANNEL_IN = 2.5, CHANNEL_OUT = 7.5;
/** Weirdness below which a column may be on a river (the gradient is only taken there). */
const RIVER_W = 0.07;
/** Snow-slope and peak biomes need at least this much height above the sea. */
const HIGH_BIOME_MIN = 30;
/** Peaks are squeezed under MAX_SURFACE from here up. */
const SOFT_TOP = 96;

/** Biomes that only make sense on high ground (1 = swap for the lowland pick when the column is low). */
const HIGH_BIOME = new Uint8Array(BIOME_LIST.length);
for (const b of [Biome.Grove, Biome.SnowySlopes, Biome.JaggedPeaks, Biome.FrozenPeaks, Biome.StonyPeaks]) HIGH_BIOME[b] = 1;

const BADLANDS_BANDS = [
  TERRACOTTA_ID, TERRACOTTA_IDS.orange, TERRACOTTA_ID, TERRACOTTA_IDS.yellow, TERRACOTTA_ID,
  TERRACOTTA_IDS.red, TERRACOTTA_IDS.orange, TERRACOTTA_IDS.white, TERRACOTTA_ID,
  TERRACOTTA_IDS.brown, TERRACOTTA_IDS.light_gray, TERRACOTTA_IDS.orange,
];

export class TerrainGenerator {
  readonly seed: number;
  private readonly cont: Climate;
  private readonly eros: Climate;
  private readonly weird: Climate;
  private readonly temp: Climate;
  private readonly humid: Climate;
  private readonly detail: Simplex;
  private readonly jagged: Simplex;
  private readonly surfaceNoise: Simplex;
  private readonly density: Simplex;
  private readonly caveA: Simplex;
  private readonly caveB: Simplex;
  private readonly noodleA: Simplex;
  private readonly noodleB: Simplex;
  private readonly cheese: Simplex;
  private readonly pillar: Simplex;
  private readonly aquifer: Simplex;
  private readonly ravine: Simplex;
  private readonly hills: Simplex;
  private readonly island: Simplex;
  private readonly structures: StructureGenerator;

  // column sample scratch (see sample())
  private sC = 0; private sE = 0; private sW = 0; private sT = 0; private sHm = 0;
  private sH = 0; private sA = 0; private sBiome: Biome = Biome.Plains;

  // per-chunk scratch, reused between jobs
  private readonly colH = new Float32Array(PSIZE * PSIZE);
  private readonly colA = new Float32Array(PSIZE * PSIZE);
  private readonly lattice = new Float32Array(LXZ * LXZ * LY);
  private readonly caveLat = new Float32Array(CXZ * CXZ * LY * CF);
  private readonly row = new Float32Array(LY);
  /** Lowest y the 3-D noise can open up, per chunk column (floors below are cave floors). */
  private readonly bandLo = new Int16Array(CHUNK_SIZE * CHUNK_SIZE);
  private readonly rows = Array.from({ length: CF }, () => new Float32Array(LY));
  private readonly rav = new Float32Array(3);
  private readonly ctx: GenCtx;

  constructor(seed: number) {
    this.seed = seed | 0;
    // Vanilla octave layouts and scales (xz_scale 0.25 applied to the periods).
    this.cont = new Climate(seed + 1, 2048, [1, 1, 2, 2, 2, 1], 0.41);
    this.eros = new Climate(seed + 2, 2048, [1, 1, 0, 1, 1], 0.38);
    this.weird = new Climate(seed + 3, 512, [1, 2, 1], 0.4);
    this.temp = new Climate(seed + 5, 4096, [1.5, 0, 1], 0.35);
    this.humid = new Climate(seed + 6, 1024, [1, 1], 0.3);
    this.detail = new Simplex(seed + 4);
    this.jagged = new Simplex(seed + 11);
    this.surfaceNoise = new Simplex(seed + 12);
    this.density = new Simplex(seed + 13);
    this.caveA = new Simplex(seed + 7);
    this.caveB = new Simplex(seed + 8);
    this.cheese = new Simplex(seed + 9);
    this.noodleA = new Simplex(seed + 14);
    this.noodleB = new Simplex(seed + 15);
    this.pillar = new Simplex(seed + 16);
    this.aquifer = new Simplex(seed + 17);
    this.ravine = new Simplex(seed + 18);
    this.hills = new Simplex(seed + 19);
    this.island = new Simplex(seed + 20);
    this.structures = new StructureGenerator(this);
    this.ctx = {
      gen: this, seed: this.seed, cx: 0, cz: 0, ox: 0, oz: 0, out: new Uint16Array(0),
      top: new Int16Array(PSIZE * PSIZE),
      biome: new Uint8Array(PSIZE * PSIZE),
      tempIdx: new Uint8Array(PSIZE * PSIZE),
      humid: new Float32Array(PSIZE * PSIZE),
      cont: new Float32Array(PSIZE * PSIZE),
      erosion: new Float32Array(PSIZE * PSIZE),
    };
  }

  /** Sample climate, surface offset, noise amplitude and biome of a column. */
  private sample(wx: number, wz: number): void {
    const C = this.cont.get(wx, wz);
    const E = this.eros.get(wx, wz);
    const W = this.weird.get(wx, wz);
    const T = this.temp.get(wx, wz);
    const Hm = this.humid.get(wx, wz);
    const aw = Math.abs(W);
    const pv = 1 - Math.abs(3 * aw - 2);            // peaks and valleys
    const ridge = clamp01((pv + 1) * 0.5);
    const detail = fbm2(this.detail, wx * 0.02, wz * 0.02, 3);
    // hill layer: knolls and dips 30 to 80 blocks across, flat in between
    const hn = this.hills.noise2(wx * 0.0085, wz * 0.0085) * 0.65 + this.hills.noise2(wx * 0.021 + 17.3, wz * 0.021 - 8.1) * 0.35;
    const hill = hn * (0.45 + 1.7 * Math.abs(hn));

    let h: number;
    let amp = 1.5;
    let island = false;
    if (C < -0.11) {
      h = table(C, OCEAN_X, OCEAN_Y) + detail * 1.5 + hn * (C < -0.4 ? 5 : 2.5);
      if (C > -0.62 && C < -0.2) {
        // small islands: the tips of rare sea mounts
        const m = this.island.noise2(wx * 0.0062, wz * 0.0062) - 0.74;
        if (m > 0) {
          const edge = smooth(-0.62, -0.5, C) * (1 - smooth(-0.27, -0.2, C));
          h += Math.min(1, m * 9) * edge * (4.5 - h + hill * 2);
          island = h >= 1;
        }
      }
    } else {
      const inland = clamp01((C + 0.11) / 0.5);
      const far = smooth(0.4, 0.55, C);
      // every erosion table shares its knots: find the segment once
      let k = 1;
      while (k < E_X.length - 1 && E >= E_X[k]) k++;
      const k0 = k - 1;
      const et = clamp01((E - E_X[k0]) / (E_X[k] - E_X[k0]));
      const lerp = (ys: readonly number[]): number => ys[k0] + (ys[k] - ys[k0]) * et;
      let level = lerp(L_COAST) + lerp(L_INLAND) * inland + lerp(L_FAR) * far;
      // high ground sinks towards the weirdness valleys, so mountains have floors
      const low = 4 + 3 * inland;
      if (level > low) level = low + (level - low) * (0.45 + 0.55 * smooth(0.05, 0.4, ridge));
      let land = level + (lerp(P_COAST) + lerp(P_INLAND) * inland) * ridge * Math.sqrt(ridge);
      // erosion band 5 is the "shattered" terrain: raised, with strong 3-D noise
      const shattered = smooth(0.45, 0.49, E) * (1 - smooth(0.51, 0.55, E)) * smooth(-0.15, 0, C);
      const rugged = smooth(0.3, -0.6, E);
      // forests roll a little more than open country
      const wooded = 1 + 0.35 * smooth(-0.1, 0.2, Hm);
      land += 14 * shattered + hill * lerp(HILLS) * (0.6 + 0.4 * inland) * wooded + detail * (0.7 + 2.4 * rugged);
      // mountain flanks break into crests and gullies instead of one smooth dome
      const jag = smooth(-0.4, -0.62, E) * smooth(0.12, 0.55, ridge) * (0.35 + 0.65 * inland);
      if (jag > 0) land += jag * 38 * (ridged2(this.jagged, wx * 0.012, wz * 0.012, 3) - 0.45);
      // 3-D noise strength: past ~10 the density folds over itself into ledges and overhangs.
      // Strong on mountain flanks and the shattered band, in patches on rough hills, weak on flat land.
      const crag = smooth(0.3, 0.7, Math.abs(hn)) * smooth(-0.02, -0.3, E);
      amp = 1.2 + 3 * smooth(0.05, -0.3, E) + 15 * smooth(-0.3, -0.7, E) * inland + 24 * shattered + 13 * crag;
      if (land > SOFT_TOP) land = SOFT_TOP + (MAX_SURFACE - SEA_LEVEL - SOFT_TOP) * (1 - Math.exp((SOFT_TOP - land) / 24));
      h = 1 + (land - 1) * smooth(-0.11, -0.03, C);
    }

    // rivers follow the zero line of weirdness. `near` is the distance to that
    // line in blocks (weirdness over its gradient), so the channel keeps one
    // width however slowly the noise crosses zero.
    let near = 1e9;
    if (aw < RIVER_W && C > -0.19) {
      const gx = this.weird.get(wx + 4, wz) - W, gz = this.weird.get(wx, wz + 4) - W;
      near = aw / Math.max(0.0012, Math.sqrt(gx * gx + gz * gz) * 0.25);
    }
    if (C > -0.19) {
      // far inland at low erosion the valley stays dry (a canyon floor) like vanilla
      const dry = smooth(-0.3, -0.45, E) * smooth(-0.02, 0.08, C);
      // flood plain: the land comes down to the banks across the valley band
      const bank = 1.5 + (Math.max(2, h * 0.4) - 1.5) * dry;
      if (h > bank) h = bank + (h - bank) * smooth(0.015, 0.12, aw);
      if (near < CHANNEL_OUT) {
        const bed = RIVER_BED + (bank - RIVER_BED) * dry;
        const open = Math.max(smooth(CHANNEL_IN, CHANNEL_OUT, near), smooth(0.045, RIVER_W, aw));
        if (h > bed) h = bed + (h - bed) * open;
      }
    }

    // the valley slice of the biome table is the river strip, not the whole band
    const sign = W < 0 ? -1 : 1;
    const wBiome = near < RIVER_HALF ? sign * 0.01 : aw < 0.05 ? sign * 0.0501 : W;
    let biome = island ? pickBiome(T, Hm, h < 2.5 ? -0.15 : 0, Math.max(E, 0.1), aw < 0.06 ? sign * 0.06 : W)
      : pickBiome(T, Hm, C, E, wBiome);
    if (HIGH_BIOME[biome] === 1 && h < HIGH_BIOME_MIN) biome = pickBiome(T, Hm, C, 0, wBiome);
    // swamps sit at the water line: half land, half shallow pools
    if ((biome === Biome.Swamp || biome === Biome.MangroveSwamp) && h > -1) h = Math.max(-1, h - 1.7);

    this.sC = C; this.sE = E; this.sW = W; this.sT = T; this.sHm = Hm;
    this.sH = Math.min(MAX_SURFACE, SEA_LEVEL + h);
    this.sA = amp;
    this.sBiome = biome;
  }

  columnInfo(wx: number, wz: number): ColumnInfo {
    this.sample(wx, wz);
    return { height: Math.floor(this.sH), biome: this.sBiome };
  }

  /** Loot table for a structure chest at this position (null = the default table). */
  structureLootAt(x: number, y: number, z: number): import('../game/loot').LootKind | null {
    return this.structures.lootAt(x, y, z);
  }

  /** Closest structures to (wx, wz) — debug HUD compass. */
  nearestStructures(wx: number, wz: number, limit: number): StructureHit[] {
    return this.structures.nearby(wx, wz, limit);
  }

  /** Biome plus the raw climate values of a column (ores, structures). */
  columnClimate(wx: number, wz: number): {
    height: number; biome: Biome; cont: number; erosion: number; weird: number; temp: number; humid: number;
  } {
    this.sample(wx, wz);
    return {
      height: Math.floor(this.sH), biome: this.sBiome,
      cont: this.sC, erosion: this.sE, weird: this.sW, temp: this.sT, humid: this.sHm,
    };
  }

  /** A dry, temperate land column near the origin to start the player on. */
  findSpawn(): { x: number; z: number } {
    for (let r = 0; r <= 6000; r += 48) {
      const steps = Math.max(1, Math.floor((r * 2 * Math.PI) / 48));
      for (let s = 0; s < steps; s++) {
        const a = (s / steps) * Math.PI * 2;
        const x = Math.round(Math.cos(a) * r) + 8;
        const z = Math.round(Math.sin(a) * r) + 8;
        this.sample(x, z);
        if (
          this.sC > 0 && this.sC < 0.6 && this.sH > SEA_LEVEL + 2 && this.sH < SEA_LEVEL + 30 &&
          Math.abs(this.sW) > 0.14 && this.sE > -0.2 && this.sT > -0.4 && this.sT < 0.5
        ) return this.nearVillage(x, z);
      }
    }
    return { x: 8, z: 8 };
  }

  /**
   * Move a spawn candidate to the edge of the closest village, when one is
   * within reach: a dry column on a ring just outside its centre, so the player
   * starts in sight of it and not inside a house.
   */
  private nearVillage(x: number, z: number): { x: number; z: number } {
    const v = this.structures.nearby(x, z, 32, 2000).find((h) => h.name === 'Village');
    if (!v) return { x, z };
    for (let r = 28; r <= 60; r += 8) {
      for (let s = 0; s < 16; s++) {
        // start on the side facing the original candidate
        const a = Math.atan2(z - v.z, x - v.x) + (s % 2 ? -1 : 1) * Math.ceil(s / 2) * (Math.PI / 8);
        const px = Math.round(v.x + Math.cos(a) * r), pz = Math.round(v.z + Math.sin(a) * r);
        if (this.dryGround(px, pz)) return { x: px, z: pz };
      }
    }
    return { x, z };
  }

  /** Is (x, z) and the ground a player is scattered over around it clear of the water line? */
  private dryGround(x: number, z: number): boolean {
    this.sample(x, z);
    if (this.sH >= SEA_LEVEL + 40) return false;
    for (let dz = -12; dz <= 12; dz += 12) {
      for (let dx = -12; dx <= 12; dx += 12) {
        this.sample(x + dx, z + dz);
        if (this.sH < SEA_LEVEL + 2.5) return false;
      }
    }
    return true;
  }

  /** Fill `out` (length CHUNK_SIZE*WORLD_HEIGHT*CHUNK_SIZE) with block ids. */
  generateChunkBlocks(cx: number, cz: number, out: Uint16Array): void {
    const ctx = this.ctx;
    ctx.cx = cx; ctx.cz = cz;
    ctx.ox = cx * CHUNK_SIZE; ctx.oz = cz * CHUNK_SIZE;
    ctx.out = out;
    out.fill(AIR);

    this.sampleColumns(ctx);
    this.structures.flatten(ctx, this.colH, this.colA);
    this.fillTerrain(ctx);
    this.carveCaves(ctx);
    this.applySurface(ctx);
    placeOres(ctx);
    decorate(ctx, this.surfaceNoise);
    this.structures.place(ctx);
  }

  private sampleColumns(ctx: GenCtx): void {
    const x0 = ctx.ox - MARGIN;
    const z0 = ctx.oz - MARGIN;
    for (let pz = 0, i = 0; pz < PSIZE; pz++) {
      for (let px = 0; px < PSIZE; px++, i++) {
        this.sample(x0 + px, z0 + pz);
        this.colH[i] = this.sH;
        this.colA[i] = this.sA;
        ctx.biome[i] = this.sBiome;
        ctx.tempIdx[i] = temperatureIndex(this.sT);
        ctx.humid[i] = this.sHm;
        ctx.cont[i] = this.sC;
        ctx.erosion[i] = this.sE;
      }
    }
  }

  /** 3-D density -> stone / water for the chunk, surface heights for the padded area. */
  private fillTerrain(ctx: GenCtx): void {
    const { colH, colA, lattice, row } = this;
    const out = ctx.out;
    let lo = WORLD_HEIGHT, hi = 0;
    for (let i = 0; i < PSIZE * PSIZE; i++) {
      const a = colA[i];
      if (colH[i] - a < lo) lo = colH[i] - a;
      if (colH[i] + a > hi) hi = colH[i] + a;
    }
    const j0 = Math.max(0, Math.floor(lo / CELL) - 1);
    const j1 = Math.min(LY - 1, Math.ceil(hi / CELL) + 1);

    const x0 = ctx.ox - MARGIN;
    const z0 = ctx.oz - MARGIN;
    const n = this.density;
    for (let iz = 0; iz < LXZ; iz++) {
      for (let ix = 0; ix < LXZ; ix++) {
        const wx = (x0 + ix * CELL) / 52;
        const wz = (z0 + iz * CELL) / 52;
        let li = (iz * LXZ + ix) * LY + j0;
        for (let j = j0; j <= j1; j++, li++) {
          const y = (j * CELL) / 20;
          lattice[li] = n.noise3(wx, y, wz) * 0.7 + n.noise3(wx * 2.1 + 9.3, y * 2.1, wz * 2.1 - 4.7) * 0.3;
        }
      }
    }

    for (let pz = 0, i = 0; pz < PSIZE; pz++) {
      const lz = pz >> 2;
      const fz = (pz & 3) / CELL;
      for (let px = 0; px < PSIZE; px++, i++) {
        const lx = px >> 2;
        const fx = (px & 3) / CELL;
        const H = colH[i];
        const A = colA[i];
        const yHi = Math.min(WORLD_HEIGHT - 2, Math.ceil(H + A));
        const yLo = Math.max(1, Math.floor(H - A));
        // noise of this column at each lattice row (bilinear in x/z)
        const b00 = (lz * LXZ + lx) * LY, b10 = b00 + LY, b01 = b00 + LXZ * LY, b11 = b01 + LY;
        const w00 = (1 - fx) * (1 - fz), w10 = fx * (1 - fz), w01 = (1 - fx) * fz, w11 = fx * fz;
        const r1 = Math.min(LY - 1, (yHi >> 2) + 1);
        for (let j = yLo >> 2; j <= r1; j++) {
          row[j] = lattice[b00 + j] * w00 + lattice[b10 + j] * w10 + lattice[b01 + j] * w01 + lattice[b11 + j] * w11;
        }

        const cxl = px - MARGIN;
        const czl = pz - MARGIN;
        const inChunk = cxl >= 0 && cxl < CHUNK_SIZE && czl >= 0 && czl < CHUNK_SIZE;
        const base = cxl + czl * CHUNK_SIZE;
        let top = yLo - 1;
        for (let y = yHi; y >= yLo; y--) {
          const j = y >> 2;
          const v = row[j] + (row[j + 1] - row[j]) * ((y & 3) / CELL);
          if (H - y + A * v > 0) {
            if (y > top) top = y;
            if (inChunk) out[base + y * 256] = STONE_ID;
            else break; // margin columns only need the surface
          } else if (inChunk && y <= SEA_LEVEL) {
            out[base + y * 256] = WATER_ID;
          }
        }
        ctx.top[i] = top;
        if (inChunk) {
          this.bandLo[base] = yLo;
          out[base] = BEDROCK_ID;
          for (let y = 1; y < yLo; y++) out[base + y * 256] = STONE_ID;
          for (let y = yHi + 1; y <= SEA_LEVEL; y++) out[base + y * 256] = WATER_ID;
        }
      }
    }
  }

  /** Water level of an aquifer cell (32x32 columns), or -1 when it is dry. */
  private aquiferLevel(cellX: number, cellZ: number): number {
    const f = this.aquifer.noise2(cellX * 0.23, cellZ * 0.23);
    if (f < 0.02) return -1;
    return Math.min(SEA_LEVEL - 11, 8 + (Math.floor((f - 0.02) * 62) & ~3));
  }

  /**
   * Ravine through a column: `out[0]` floor y, `out[1]` roof y, `out[2]` how
   * close the column is to the ravine's centre line (1 on it, 0 at the rim).
   * Returns false when no ravine crosses. Pure in (seed, column).
   */
  private ravineAt(wx: number, wz: number, out: Float32Array): boolean {
    const n = this.ravine;
    // ravines only exist inside sparse blobs of a slow mask, so they start and end
    const mask = n.noise2(wx * 0.0035 + 91, wz * 0.0035 - 57);
    if (mask < 0.38) return false;
    // the centre line is the zero contour of a warped noise
    const warp = n.noise2(wx * 0.021 - 13, wz * 0.021 + 29) * 0.05;
    const line = Math.abs(n.noise2(wx * 0.0052, wz * 0.0052) + warp);
    const width = 0.036 * Math.min(1, (mask - 0.38) * 9);
    if (line >= width) return false;
    const floor = 9 + (n.noise2(wx * 0.011 + 400, wz * 0.011 + 400) + 1) * 5;
    out[0] = floor;
    out[1] = floor + 26 + mask * 14;
    out[2] = 1 - line / width;
    return true;
  }

  /**
   * Noise caves (spaghetti, noodle, cheese with pillars), ravines, and
   * aquifer-lite: carved cells below the local water level hold water, and
   * below LAVA_Y lava.
   */
  private carveCaves(ctx: GenCtx): void {
    const out = ctx.out;
    const lat = this.caveLat;
    let maxTop = 0;
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const t = ctx.top[x + MARGIN + (z + MARGIN) * PSIZE];
        if (t > maxTop) maxTop = t;
      }
    }
    const j1 = Math.min(LY - 1, (maxTop >> 2) + 1);
    for (let iz = 0; iz < CXZ; iz++) {
      for (let ix = 0; ix < CXZ; ix++) {
        const wx = ctx.ox + ix * CELL;
        const wz = ctx.oz + iz * CELL;
        let li = (iz * CXZ + ix) * LY * CF;
        for (let j = 0; j <= j1; j++, li += CF) {
          const y = j * CELL;
          lat[li] = this.caveA.noise3(wx * 0.014, y * 0.028, wz * 0.014);
          lat[li + 1] = this.caveB.noise3(wx * 0.014, y * 0.028, wz * 0.014);
          // noodles only exist where a slow toggle noise is positive
          const toggle = this.pillar.noise3(wx * 0.004 + 31, y * 0.004, wz * 0.004 - 17);
          lat[li + 2] = this.noodleA.noise3(wx * 0.03, y * 0.045, wz * 0.03) + (toggle < 0 ? 1 : 0);
          lat[li + 3] = this.noodleB.noise3(wx * 0.03, y * 0.045, wz * 0.03);
          const c = this.cheese.noise3(wx * 0.0095, y * 0.017, wz * 0.0095) * 0.8
            + this.cheese.noise3(wx * 0.033 + 5, y * 0.05, wz * 0.033 + 5) * 0.2;
          // tall thin pillars left standing inside the big chambers
          const p = this.pillar.noise3(wx * 0.06, y * 0.006, wz * 0.06);
          lat[li + 4] = c - Math.max(0, p - 0.5) * 3;
          // spaghetti tunnels swell and pinch along their length
          lat[li + 5] = this.aquifer.noise3(wx * 0.012 + 71, y * 0.02, wz * 0.012 - 71);
        }
      }
    }

    const cellX = ctx.ox >> 5;
    const cellZ = ctx.oz >> 5;
    const level = this.aquiferLevel(cellX, cellZ);
    const rows = this.rows;
    const rav = this.rav;
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const pi = x + MARGIN + (z + MARGIN) * PSIZE;
        const top = ctx.top[pi];
        // never open caves into (or right beside) the sea, rivers and lakes
        let maxY = top;
        let low = top;
        for (let dz = -3; dz <= 3; dz++) {
          for (let dx = -3; dx <= 3; dx++) {
            const t = ctx.top[pi + dx + dz * PSIZE];
            if (t < low) low = t;
          }
        }
        if (low < SEA_LEVEL) maxY = Math.min(maxY, low - 5);
        if (maxY < 1) continue;

        // a 2-thick stone wall separates aquifer cells with different levels
        const wxw = ctx.ox + x, wzw = ctx.oz + z;
        const ex = wxw & 31, ez = wzw & 31;
        let wall = -1;
        if (ex === 0 || ex === 31) {
          const o = this.aquiferLevel(cellX + (ex === 0 ? -1 : 1), cellZ);
          if (o !== level) wall = Math.max(wall, o, level);
        }
        if (ez === 0 || ez === 31) {
          const o = this.aquiferLevel(cellX, cellZ + (ez === 0 ? -1 : 1));
          if (o !== level) wall = Math.max(wall, o, level);
        }

        const lx = x >> 2, lz = z >> 2;
        const fx = (x & 3) / CELL, fz = (z & 3) / CELL;
        const b00 = (lz * CXZ + lx) * LY * CF, b10 = b00 + LY * CF, b01 = b00 + CXZ * LY * CF, b11 = b01 + LY * CF;
        const w00 = (1 - fx) * (1 - fz), w10 = fx * (1 - fz), w01 = (1 - fx) * fz, w11 = fx * fz;
        const r1 = Math.min(LY - 1, (maxY >> 2) + 1);
        for (let j = 0; j <= r1; j++) {
          const o = j * CF;
          for (let f = 0; f < CF; f++) {
            rows[f][j] = lat[b00 + o + f] * w00 + lat[b10 + o + f] * w10 + lat[b01 + o + f] * w01 + lat[b11 + o + f] * w11;
          }
        }

        const ravine = this.ravineAt(wxw, wzw, rav);
        const rFloor = rav[0], rRoof = rav[1], rNear = rav[2];

        const base = x + z * CHUNK_SIZE;
        for (let y = 1; y <= maxY; y++) {
          const i = base + y * 256;
          if (out[i] !== STONE_ID || y <= wall) continue;
          const j = y >> 2;
          const fy = (y & 3) / CELL;
          const depth = top - y;
          const a = rows[0][j] + (rows[0][j + 1] - rows[0][j]) * fy;
          const b = rows[1][j] + (rows[1][j + 1] - rows[1][j]) * fy;
          const thick = rows[5][j] + (rows[5][j + 1] - rows[5][j]) * fy;
          // tunnels narrow as they approach the surface so entrances stay rare
          let carve = a * a + b * b < (depth < 6 ? 0.005 : 0.0115 + thick * 0.0075);
          if (!carve && depth > 6) {
            const na = rows[2][j] + (rows[2][j + 1] - rows[2][j]) * fy;
            const nb = rows[3][j] + (rows[3][j + 1] - rows[3][j]) * fy;
            carve = na * na + nb * nb < 0.004;
            if (!carve && depth > 10) {
              // cheese caverns: more and larger the deeper they are
              const c = rows[4][j] + (rows[4][j + 1] - rows[4][j]) * fy;
              carve = c > 0.47 - Math.max(0, 40 - y) * 0.004;
            }
          }
          if (!carve && ravine && y >= rFloor && y <= rRoof) {
            // V profile: a narrow floor widening upward, pinched shut at the roof
            const t = (y - rFloor) / (rRoof - rFloor);
            carve = rNear > 1 - (0.25 + 0.75 * Math.sqrt(t)) * Math.min(1, (1 - t) * 5);
          }
          if (carve) out[i] = y <= LAVA_Y ? LAVA_ID : y <= level ? WATER_ID : AIR;
        }
      }
    }
  }

  /** Terracotta strata of the badlands, warped a little per column. */
  private strata(wx: number, wy: number, wz: number): number {
    const band = wy + Math.round(this.surfaceNoise.noise2(wx * 0.01, wz * 0.01) * 3);
    return BADLANDS_BANDS[((band % BADLANDS_BANDS.length) + BADLANDS_BANDS.length) % BADLANDS_BANDS.length];
  }

  private badlands(depth: number, wx: number, y: number, wz: number): number {
    if (depth > 24) return STONE_ID;
    if (y <= SEA_LEVEL + 8) return depth <= 2 ? RED_SAND_ID : depth <= 5 ? RED_SANDSTONE_ID : this.strata(wx, y, wz);
    return depth === 1 ? TERRACOTTA_IDS.orange : this.strata(wx, y, wz);
  }

  /**
   * Surface rule: the block for a terrain cell `depth` blocks below a floor
   * (1 = the exposed top). `s` is the column's surface noise, `steep` the
   * largest drop to a neighbouring column.
   */
  private surfaceBlock(
    surf: Surf, depth: number, wx: number, y: number, wz: number, s: number, steep: number, biome: Biome
  ): number {
    const top = depth === 1;
    switch (surf) {
      case Surf.Sand:
        return depth <= 4 ? SAND_ID : depth <= 8 ? SANDSTONE_ID : STONE_ID;
      case Surf.Stone:
        return STONE_ID;
      case Surf.StonyShore:
        return s > 0.3 && depth <= 2 ? GRAVEL_ID : STONE_ID;
      case Surf.Gravel:
        return depth <= 3 ? GRAVEL_ID : STONE_ID;
      case Surf.GravellyHills:
        if (s > 0.4) return STONE_ID;
        if (s < -0.35) return top ? GRASS_ID : depth <= 4 ? DIRT_ID : STONE_ID;
        return depth <= 3 ? GRAVEL_ID : STONE_ID;
      case Surf.WindsweptHills:
        if (s > 0.3) return STONE_ID;
        break;
      case Surf.SnowyGrass:
        return top ? SNOW_ID : depth <= 4 ? DIRT_ID : STONE_ID;
      case Surf.Snow:
        if (steep >= 4) return STONE_ID;
        return depth <= 3 ? SNOW_BLOCK_ID : STONE_ID;
      case Surf.Grove:
        if (top) return s > 0.45 ? POWDER_SNOW_ID : SNOW_BLOCK_ID;
        return depth <= 2 ? SNOW_BLOCK_ID : depth <= 5 ? DIRT_ID : STONE_ID;
      case Surf.JaggedPeaks:
        return steep >= 3 || depth > 3 ? STONE_ID : SNOW_BLOCK_ID;
      case Surf.FrozenPeaks:
        if (steep >= 3) return depth <= 3 ? PACKED_ICE_ID : STONE_ID;
        if (depth > 3) return STONE_ID;
        return s > 0.4 ? PACKED_ICE_ID : s < -0.55 ? ICE_ID : SNOW_BLOCK_ID;
      case Surf.StonyPeaks:
        // calcite strips follow contour lines of the surface noise
        return Math.abs(s) < 0.07 && depth <= 4 ? CALCITE_ID : STONE_ID;
      case Surf.Mycelium:
        return top ? MYCELIUM_ID : depth <= 4 ? DIRT_ID : STONE_ID;
      case Surf.Mud:
        return depth <= 3 ? MUD_ID : depth <= 5 ? DIRT_ID : STONE_ID;
      case Surf.Podzol:
        if (top) return s > 0.1 ? PODZOL_ID : s < -0.45 ? COARSE_DIRT_ID : GRASS_ID;
        return depth <= 4 ? DIRT_ID : STONE_ID;
      case Surf.WoodedBadlands:
        if (y > SEA_LEVEL + 20) {
          if (top) return s > 0.25 ? COARSE_DIRT_ID : GRASS_ID;
          if (depth <= 3) return DIRT_ID;
        }
        return this.badlands(depth, wx, y, wz);
      case Surf.Badlands:
        return this.badlands(depth, wx, y, wz);
      default:
        break;
    }
    // grass family; ground above the biome's snow line gets a snow cap, the
    // same height the weather starts snowing at (never, for the warm biomes)
    if (top) return isColdAt(biome, y - s * 4) ? SNOW_ID : GRASS_ID;
    return depth <= 4 ? DIRT_ID : STONE_ID;
  }

  /** Surface rules, sea-floor materials, deepslate and frozen water. */
  private applySurface(ctx: GenCtx): void {
    const out = ctx.out;
    const sn = this.surfaceNoise;
    for (let z = 0; z < CHUNK_SIZE; z++) {
      for (let x = 0; x < CHUNK_SIZE; x++) {
        const wx = ctx.ox + x;
        const wz = ctx.oz + z;
        const pi = x + MARGIN + (z + MARGIN) * PSIZE;
        const top = ctx.top[pi];
        const def = BIOME_LIST[ctx.biome[pi]];
        const s = sn.noise2(wx * 0.045, wz * 0.045);
        const steep = Math.max(
          top - ctx.top[pi - 1], top - ctx.top[pi + 1], top - ctx.top[pi - PSIZE], top - ctx.top[pi + PSIZE]
        );
        // water within three blocks: only there does low grass turn into a beach strip
        const shore = top <= SEA_LEVEL && (
          ctx.top[pi - 3] < SEA_LEVEL || ctx.top[pi + 3] < SEA_LEVEL ||
          ctx.top[pi - 3 * PSIZE] < SEA_LEVEL || ctx.top[pi + 3 * PSIZE] < SEA_LEVEL ||
          ctx.top[pi - 1] < SEA_LEVEL || ctx.top[pi + 1] < SEA_LEVEL ||
          ctx.top[pi - PSIZE] < SEA_LEVEL || ctx.top[pi + PSIZE] < SEA_LEVEL
        );
        const base = x + z * CHUNK_SIZE;
        const deepslate = DEEPSLATE_Y + ((hash2(wx, wz, this.seed ^ 0xd5) * 4) | 0);
        const surfaceFloor = Math.min(top - 8, this.bandLo[base]); // deeper floors are cave floors: plain stone

        let depth = 0;
        let wet = false; // the current run of solid sits under water
        for (let y = Math.max(top, SEA_LEVEL); y >= 1; y--) {
          const i = base + y * 256;
          const id = out[i];
          if (id !== STONE_ID) {
            depth = 0;
            wet = id === WATER_ID;
            continue;
          }
          depth++;
          if (y < deepslate) {
            out[i] = DEEPSLATE_ID;
          } else if (y < surfaceFloor) {
            continue;
          } else if (wet) {
            if (depth > 4) continue;
            const shallow = y >= SEA_LEVEL - 3;
            if (def.surface === Surf.Mud) out[i] = MUD_ID;
            else if (def.bed === Bed.Sand || (shallow && def.surface === Surf.Sand)) out[i] = depth <= 3 ? SAND_ID : SANDSTONE_ID;
            else if (def.bed === Bed.Dirt) {
              // river and swamp beds: dirt with clay, sand and gravel disks
              const d = sn.noise2(wx * 0.09 + 40, wz * 0.09 - 40);
              out[i] = d > 0.45 ? CLAY_ID : d < -0.45 ? GRAVEL_ID : d > 0.15 ? SAND_ID : DIRT_ID;
            } else {
              const d = sn.noise2(wx * 0.06 - 70, wz * 0.06 + 70);
              out[i] = shallow && d > 0 ? SAND_ID : d > 0.55 ? CLAY_ID : d < -0.5 ? SAND_ID : GRAVEL_ID;
            }
          } else {
            const block = this.surfaceBlock(def.surface, depth, wx, y, wz, s, steep, ctx.biome[pi]);
            // a beach strip where ordinary land meets the water line
            out[i] = block === GRASS_ID && y <= SEA_LEVEL && depth === 1 && shore ? (def.bed === Bed.Dirt ? DIRT_ID : SAND_ID) : block;
          }
        }

        // freeze the top layer of water in frozen biomes (patchy on the open ocean)
        if (def.frozen && top < SEA_LEVEL && out[base + SEA_LEVEL * 256] === WATER_ID) {
          const open = ctx.biome[pi] === Biome.FrozenOcean || ctx.biome[pi] === Biome.DeepFrozenOcean;
          if (!open || sn.noise2(wx * 0.02 + 11, wz * 0.02 + 11) > -0.15) out[base + SEA_LEVEL * 256] = ICE_ID;
        }
      }
    }
  }
}
